import clsx from "clsx";
import { RootState } from "../store";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import {
  addPoint,
  setPointVisibility,
  setSelectedPointId,
  initializePoints,
  Point,
} from "../store/pointsSlice";

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";

import {
  FaEdit,
  FaTrash,
  FaArrowLeft,
  FaMapMarkerAlt,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa"; // Icons from react-icons

import PointEditor from "../components/PointEditor";
import { useTranslation } from 'react-i18next';
import { useAddToCart } from '../utils/useAddToCart'; // S'assurer que cet import est présent
import { HiShoppingCart } from "react-icons/hi";
import { EditorPreviewRef } from "../components/EditorPreview"; // Ajout de l'import

interface PointsProps {
  editorPreviewRef: React.RefObject<EditorPreviewRef | null>; // Renommer mapEditorRef en editorPreviewRef et MAJ type
}

const Points: React.FC<PointsProps> = ({ editorPreviewRef }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(); // Garder pour les autres traductions de la page

  const allPointsFromState = useSelector((state: RootState) => state.points.points);
  const selectedPointId = useSelector(
    (state: RootState) => state.points.selectedPointId
  );
  const activities = useSelector((state: RootState) => state.activities.activities);
  const activeActivityIds = useSelector((state: RootState) => state.activities.activeActivityIds);
  const [selectedAvailablePoint, setSelectedAvailablePoint] = useState<
    string | null
  >(null);

  const { handleAddToCart, isAddingToCart, cartOverlay } = useAddToCart(editorPreviewRef);

  useEffect(() => {
    console.log("[Points.tsx] All points from Redux state:", allPointsFromState.length, JSON.stringify(allPointsFromState, null, 2));
    console.log("[Points.tsx] Active Activity IDs:", activeActivityIds);

    // Recalculate displayedPoints here for logging clarity
    const currentDisplayedPoints = allPointsFromState.filter(
      (point: Point) => activeActivityIds.includes(point.activityId) && point.isVisible
    );
    console.log("[Points.tsx] Calculated displayedPoints:", currentDisplayedPoints.length, JSON.stringify(currentDisplayedPoints, null, 2));

  }, [allPointsFromState, activeActivityIds]); // Log whenever the source data changes

  useEffect(() => {
    // Filtrer les activités actives
    const activeActivities = activities.filter((activity) =>
      activeActivityIds.includes(activity.id)
    );

    // Initialiser les points uniquement pour les activités actives
    dispatch(initializePoints(activeActivities));

    // Supprimer les points des activités supprimées
    allPointsFromState.filter(point => !activities.some(activity => activity.id === point.activityId)).forEach(point => {
      dispatch(setPointVisibility({ id: point.id, isVisible: false }));
    });
  }, [dispatch, activities, activeActivityIds, activities.length]); // Add activities.length here

  const handleDeletePoint = (id: string) => {
    dispatch(setPointVisibility({ id: id, isVisible: false }));
  };

  const handleAddPoint = (id: string) => {
    dispatch(addPoint(id));
    dispatch(setPointVisibility({ id: id, isVisible: true }));
    setSelectedAvailablePoint(null); // Reset selected value after adding
  };

  const handleSelectPoint = (id: string) => {
    dispatch(setSelectedPointId(id));
  };

  const handleReturnToList = () => {
    dispatch(setSelectedPointId(null));
  };

  const unvisiblePoints = allPointsFromState.filter(
    (point: Point) => !point.isVisible && activeActivityIds.includes(point.activityId)
  );

  // Calculate displayedPoints for rendering : inclure tous les points des activités actives, qu'ils soient visibles ou non
  const displayedPoints = allPointsFromState.filter(
    (point: Point) => activeActivityIds.includes(point.activityId)
  );

  // --- Find lowest/highest elevation points ---
  const lowestPoint = allPointsFromState.find(
    (p: Point) => p.type === 'lowestElevation' && activeActivityIds.includes(p.activityId)
  );
  const highestPoint = allPointsFromState.find(
    (p: Point) => p.type === 'highestElevation' && activeActivityIds.includes(p.activityId)
  );

  // --- Toggle handlers ---
  const handleToggleLowest = () => {
    if (lowestPoint) {
      dispatch(setPointVisibility({ id: lowestPoint.id, isVisible: !lowestPoint.isVisible }));
    }
  };
  const handleToggleHighest = () => {
    if (highestPoint) {
      dispatch(setPointVisibility({ id: highestPoint.id, isVisible: !highestPoint.isVisible }));
    }
  };

  return (
    <div className="w-full w-[700px] max-w-[700px] mx-auto px-4 space-y-6">
      {cartOverlay}
      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-lg font-semibold font-sans text-white">
          {t('points.title')}
        </h1>
        <p className="text-gray-400 font-light text-sm">
          {t('points.subtitle')}
        </p>
      </div>


      {/* Conditionnellement afficher l'éditeur ou la liste */}
      {selectedPointId ? (
        /* Éditeur de point (affiché seul) */
        <div className="space-y-8">
          <button
            onClick={handleReturnToList}
            className="inline-flex items-center text-white hover:opacity-75 cursor-pointer transition duration-150 ease-in-out"
          >
            <FaArrowLeft className="mr-2" /> {t('points.back_to_list')}
          </button>
          <PointEditor pointId={selectedPointId} editorPreviewRef={editorPreviewRef} />
        </div>
      ) : (
        /* Liste des points (masquée lors de l'édition) */
        <div className="space-y-3">
          {/* Top Bar (Select All, Delete, Add) */}
          <div className="flex items-center justify-between">
            <div className="text-white font-medium">
              {t('points.points')} ({displayedPoints.length})
            </div>

            <div className="flex items-center space-x-2">
              {/* Delete Button */}
              <button
                onClick={() =>
                  displayedPoints
                    .filter((point) => point.isVisible)
                    .forEach((point) => handleDeletePoint(point.id))
                }
                className="flex flex-col items-center justify-center p-2 rounded-md bg-[#333] h-8 w-8 hover:opacity-75 cursor-pointer text-gray-400 hover:text-white transition duration-150 ease-in-out"
                title={t('points.delete_all')}
                disabled={
                  displayedPoints.filter((point) => point.isVisible).length ===
                  0
                }
              >
                <FaTrash className="h-3 w-3" />
              </button>

              {/* Add Button (Ajouter un point) */}
              <div className="w-full min-w-0 hidden"> {/* Bouton Ajouter un point masqué */}
                <Listbox
                  value={selectedAvailablePoint}
                  onChange={handleAddPoint}
                >
                  <ListboxButton
                    className={clsx(
                      "relative block w-full rounded-lg bg-[#333] py-1.5 pr-8 pl-3 text-left text-sm/6 text-white",
                      "focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25"
                    )}
                  >
                    <span className="block truncate">
                      {selectedAvailablePoint
                        ? allPointsFromState.find((p) => p.id === selectedAvailablePoint)
                            ?.name
                        : t('points.add_point')}
                    </span>
                    <ChevronDownIcon
                      className="group pointer-events-none absolute top-2.5 right-2.5 size-4 fill-white/60"
                      aria-hidden="true"
                    />
                  </ListboxButton>
                  {unvisiblePoints.length > 0 && (
                    <ListboxOptions
                      anchor="bottom"
                      transition
                      className={clsx(
                        "w-full z-50 mt-1 rounded-xl border border-white/5 bg-[#333] p-1 [--anchor-gap:var(--spacing-1)] focus:outline-none",
                        "transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0"
                      )}
                    >
                      {unvisiblePoints.map((point) => (
                        <ListboxOption
                          key={point.id}
                          value={point.id}
                          className="group flex cursor-default items-center gap-2 rounded-lg py-1.5 px-3 select-none data-[focus]:bg-white/10"
                        >
                          <div className="text-sm/6 text-white">
                            {point.name}
                          </div>
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  )}
                </Listbox>
              </div>
            </div>
          </div>

          {/* Liste des points */}
          <div className="flex flex-col space-y-2 w-full max-w-full">
            {displayedPoints.length > 0 &&
              displayedPoints.map((point, index) => (
                <div
                  key={point.id}
                  className={
                    "flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 py-2.5 px-3 rounded-md transition duration-150 ease-in-out cursor-pointer border border-gray-700/50 hover:bg-gray-700/50 w-full min-w-0"
                  }
                  onClick={() => handleSelectPoint(point.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    (e.key === "Enter" || e.key === " ") &&
                    handleSelectPoint(point.id)
                  }
                >
                  <div className="flex-shrink-0 w-7 h-7 bg-gray-600 hidden sm:flex items-center justify-center rounded-sm text-gray-400 mx-auto sm:mx-0">
                    <FaMapMarkerAlt className="w-4 h-4" />
                  </div>
                  <div className="flex-grow min-w-0">
                    <div className="flex items-center space-x-1.5">
                      <p
                        className="text-white font-medium text-sm truncate"
                        title={point.name}
                      >
                        {index + 1}. {point.name}
                      </p>
                    </div>
                    <p className="text-gray-400 text-xs text-left break-words">
                      {t('points.activity')}: {activities.find((act) => act.id === point.activityId)
                        ?.name || t('points.unnamed_activity')}
                    </p>
                    <p className="text-gray-400 text-xs text-left break-words">
                      {t('points.coordinates')}: {point.coordinate?.[0]?.toFixed(5)}, {point.coordinate?.[1]?.toFixed(5)}
                    </p>
                  </div>

                  <div className="flex flex-row sm:flex-col items-center gap-1 sm:gap-2 mt-2 sm:mt-0 self-end sm:self-auto">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectPoint(point.id);
                      }}
                      className="flex-shrink-0 p-1 w-7 h-7 flex flex-col items-center justify-center cursor-pointer rounded-full text-gray-500 hover:text-blue-500 hover:bg-gray-700 transition duration-150 ease-in-out"
                      aria-label={t('points.edit_aria', { name: point.name })}
                      title={t('points.edit_title')}
                    >
                      <FaEdit className="w-3 h-3" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePoint(point.id);
                      }}
                      className="flex-shrink-0 p-1 w-7 h-7 flex flex-col items-center justify-center cursor-pointer rounded-full text-gray-500 hover:text-red-500 hover:bg-gray-700 transition duration-150 ease-in-out"
                      aria-label={t('points.delete_aria', { name: point.name })}
                      title={t('points.delete_title')}
                    >
                      <FaTrash className="w-3 h-3" />
                    </button>
                    {/* Eye button for ALL points */}
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        dispatch(setPointVisibility({ id: point.id, isVisible: !point.isVisible }));
                      }}
                      className={`flex-shrink-0 p-1 w-7 h-7 flex flex-col items-center justify-center cursor-pointer rounded-md border-0 transition-colors duration-150 ${point.isVisible ? 'bg-blue-600' : 'bg-gray-700'} text-white`}
                      aria-label={t('points.toggle_visibility', { name: point.name })}
                      title={t('points.toggle_visibility', { name: point.name })}
                    >
                      {point.isVisible ? <FaEye className="w-4 h-4" /> : <FaEyeSlash className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
      <button
        onClick={handleAddToCart}
        disabled={isAddingToCart}
        className="w-full text-sm cursor-pointer flex justify-center items-center space-x-2 text-white py-2 rounded-sm disabled:opacity-50 disabled:cursor-wait mt-8"
        style={{ backgroundColor: isAddingToCart ? '#FC4C02' : '#FC4C02', opacity: isAddingToCart ? 0.5 : 1 }}
        onMouseOver={e => (e.currentTarget.style.backgroundColor = '#d63a00')}
        onMouseOut={e => (e.currentTarget.style.backgroundColor = '#FC4C02')}
      >
        <HiShoppingCart className="w-5 h-5" />
        <span>{t('common.addToCart', 'Ajouter au panier')}</span>
      </button>
    </div>
  );
};

export default Points;